import { TFunction } from 'i18next';
import { SubcontractMessageField } from 'models/client-directory';
import { formatDate } from 'utils/format';

/** One entry of the parent's communication: a comment, a chat message, a note. */
export type MessageEntry = {
    key: string;
    author?: string;
    date?: string | number | Date;
    text: string;
};

export type MessageSection = {
    field: SubcontractMessageField;
    entries: MessageEntry[];
};

/**
 * DD-3689: the parent's communication the lab can build a message from, in the order the
 * flow shows it. `title` names the box for the lab; `heading` introduces the part in the
 * message.
 */
export const MESSAGE_FIELD_OPTIONS: { field: SubcontractMessageField; title: string; heading: string }[] = [
    {
        field: SubcontractMessageField.ORDER_COMMENTS,
        title: 'orders:SUBCONTRACT_ORDER_COMMENTS',
        heading: 'orders:SUBCONTRACT_MESSAGE_HEADING_ORDER_COMMENTS',
    },
    {
        field: SubcontractMessageField.INTERNAL_NOTES,
        title: 'orders:INTERNAL_NOTES',
        heading: 'orders:SUBCONTRACT_MESSAGE_HEADING_INTERNAL_NOTES',
    },
    {
        field: SubcontractMessageField.EXTERNAL_INSTRUCTIONS,
        title: 'orders:SUBCONTRACT_EXTERNAL_INSTRUCTIONS',
        heading: 'orders:SUBCONTRACT_MESSAGE_HEADING_EXTERNAL_INSTRUCTIONS',
    },
    {
        field: SubcontractMessageField.CHAT,
        title: 'messages:CHAT',
        heading: 'orders:SUBCONTRACT_MESSAGE_HEADING_CHAT',
    },
    {
        field: SubcontractMessageField.LAB_CHAT,
        title: 'messages:LAB_CHAT',
        heading: 'orders:SUBCONTRACT_MESSAGE_HEADING_LAB_CHAT',
    },
];

/** What a partner without its own choice gets: nothing, so the message starts blank. */
export const DEFAULT_MESSAGE_FIELDS: SubcontractMessageField[] = [];

const entryText = (entry: MessageEntry) => {
    const byline = [entry.date ? formatDate(new Date(entry.date), 'datetime') : '', entry.author]
        .filter(Boolean)
        .join(' – ');
    return byline ? `${byline}\n${entry.text.trim()}` : entry.text.trim();
};

/**
 * The message built from the selected parts, in the flow's order. A single part is sent as
 * it is; several get a heading each. Parts with nothing in them are left out. Translating
 * the result is a separate step the lab asks for.
 */
export const buildSubcontractMessage = (
    sections: MessageSection[],
    selectedFields: SubcontractMessageField[],
    translateIn: TFunction,
): string => {
    const parts = MESSAGE_FIELD_OPTIONS.filter((option) => selectedFields.includes(option.field))
        .map((option) => ({
            heading: translateIn(option.heading),
            entries: (sections.find((section) => section.field === option.field)?.entries ?? []).filter((entry) =>
                entry.text.trim(),
            ),
        }))
        .filter((part) => part.entries.length);
    if (parts.length === 1) return parts[0].entries.map(entryText).join('\n\n');
    return parts.map((part) => [`${part.heading}:`, ...part.entries.map(entryText)].join('\n')).join('\n\n');
};
